import React, { useEffect, useState } from 'react';
import { Bell, Send, Users, CheckCircle, XCircle, Loader, Smartphone } from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import type { SendNotificationParams } from '../../hooks/usePushNotifications';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface NotificationSenderProps {
  config: any;
}

interface SendResult {
  success: boolean;
  total_sent?: number;
  total_success?: number;
  total_failed?: number;
  error?: string;
}

export const NotificationSender: React.FC<NotificationSenderProps> = ({ config }) => {
  const { sendNotification, loadSubscriberCount, subscriberCount, isSubscribed } = usePushNotifications();

  const [form, setForm] = useState<SendNotificationParams>({
    title: '',
    body: '',
    url: '',
    icon: '',
    sent_by: 'admin',
  });
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  useEffect(() => {
    loadSubscriberCount();
  }, [loadSubscriberCount]);

  const majorColor = config?.['major-color'] || '#6B7280';
  const darkColor = config?.['dark-color'] || '#1F2937';
  const borderRadius = config?.border_radius || '0.5rem';

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setSending(true);
    setResult(null);
    const res = await sendNotification({
      ...form,
      url: form.url || '/',
      icon: form.icon || '/icon/icon_192.png',
    });
    setResult(res);
    setSending(false);
    if (res.success) {
      setForm({ title: '', body: '', url: '', icon: '', sent_by: 'admin' });
    }
  };

  const handleTestOnDevice = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    setTestSending(true);
    setResult(null);

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const reg = registrations[0];
      if (!reg) {
        setResult({ success: false, error: 'Aucun service worker actif sur cet appareil' });
        setTestSending(false);
        return;
      }

      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setResult({ success: false, error: 'Cet appareil n\'est pas abonné aux notifications' });
        setTestSending(false);
        return;
      }

      const key = sub.getKey('p256dh');
      const authKey = sub.getKey('auth');
      if (!key || !authKey) {
        setResult({ success: false, error: 'Clés de subscription introuvables' });
        setTestSending(false);
        return;
      }

      const p256dh = btoa(String.fromCharCode(...new Uint8Array(key)));
      const auth = btoa(String.fromCharCode(...new Uint8Array(authKey)));

      const res = await fetch(`${supabaseUrl}/functions/v1/send-push-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: form.title,
          body: form.body,
          url: form.url || '/',
          icon: form.icon || '/icon/icon_192.png',
          sent_by: 'test',
          target_endpoint: sub.endpoint,
          target_p256dh: p256dh,
          target_auth: auth,
        }),
      });

      const data = await res.json();
      setResult(data.success
        ? { success: true, total_sent: 1, total_success: 1, total_failed: 0 }
        : { success: false, error: data.error || 'Erreur lors du test' }
      );
    } catch (err) {
      setResult({ success: false, error: String(err) });
    }

    setTestSending(false);
  };

  const inputClass =
    'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400 transition-colors bg-white';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div
        className="px-4 py-3 flex items-center gap-2"
        style={{ backgroundColor: majorColor }}
      >
        <Bell size={18} className="text-white" />
        <span className="text-white font-semibold text-sm">Envoyer une notification push</span>
        {subscriberCount !== null && (
          <span className="ml-auto flex items-center gap-1 text-white text-xs opacity-80">
            <Users size={13} />
            {subscriberCount} abonné{subscriberCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <form onSubmit={handleSend} className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Titre *</label>
          <input
            type="text"
            className={inputClass}
            placeholder="Ex: Offre spéciale ce soir"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            maxLength={100}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Message *</label>
          <textarea
            className={`${inputClass} resize-none`}
            placeholder="Ex: Profitez de notre offre exclusive jusqu'à 22h..."
            value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            rows={3}
            maxLength={200}
            required
          />
          <div className="text-right text-xs text-gray-400 mt-0.5">{form.body.length}/200</div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">URL au clic (optionnel)</label>
          <input
            type="text"
            className={inputClass}
            placeholder="Ex: /restaurant ou https://..."
            value={form.url}
            onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
          />
        </div>

        {result && (
          <div
            className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
              result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
            style={{ borderRadius }}
          >
            {result.success ? (
              <>
                <CheckCircle size={16} />
                <span>
                  Envoyé : {result.total_success}/{result.total_sent} appareils atteints
                </span>
              </>
            ) : (
              <>
                <XCircle size={16} />
                <span>{result.error || 'Erreur lors de l\'envoi'}</span>
              </>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={sending || testSending || !form.title.trim() || !form.body.trim()}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-white text-sm font-medium rounded-lg transition-opacity disabled:opacity-50"
            style={{ backgroundColor: majorColor, borderRadius }}
          >
            {sending ? (
              <>
                <Loader size={16} className="animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Send size={16} />
                Envoyer à tous
              </>
            )}
          </button>

          {isSubscribed && (
            <button
              type="button"
              onClick={handleTestOnDevice}
              disabled={sending || testSending || !form.title.trim() || !form.body.trim()}
              title="Tester sur cet appareil uniquement"
              className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
              style={{
                borderColor: majorColor,
                color: majorColor,
                borderRadius,
              }}
            >
              {testSending ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Smartphone size={16} />
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
